import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { usePasteAssetClipboard } from '@frameio/assets/finder';
import { useIsFolderMoveTarget } from '@frameio/assets/operations';
import { useFolderPermissions } from '@frameio/permissions';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useForceGlobalDndRemeasure } from '@frameio/shared/dnd';
import { getLink } from '@frameio/shared/routing';
import { IconButton2, Text, theme, Tooltip } from '@frameio/vapor';
import { IconFolderImage, IconPlus } from '@frameio/vapor-icons';
import {
  useFolderDndNamespace,
  useIsActiveItem,
  useIsContextMenuDisabled,
  useIsFolderUploadTarget,
  useOnItemSelect,
  useOpenNavigationMenu,
  useOverrideRootFolderName,
  useShouldRenderLinks,
  useToggleItem,
} from '../contexts';
import type { ProjectTreeItem, RootFolderTreeItemProps } from '../types';
import { LONG_HOLD_TOGGLE_DELAY } from './Folder';
import TEXT from './generated/TEXT/RootFolder';
import {
  BaseContainer,
  DraggableContainer,
  HiddenLink,
  ICON_WIDTH,
  ItemContainer,
} from './TreeItem';

export const RootFolder = ({
  expandable = false,
  id,
  isOpen,
  transition,
  type,
}: RootFolderTreeItemProps) => {
  const item = useMemo<ProjectTreeItem>(
    () => ({
      id,
      type,
    }),
    [id, type]
  );

  const projectId = useActiveProjectId();
  const isActive = useIsActiveItem(item);
  const onItemSelect = useOnItemSelect();
  const shouldRenderLinks = useShouldRenderLinks();
  const dndNamespace = useFolderDndNamespace();
  const overrideRootFolderName = useOverrideRootFolderName();
  const isFolderMoveTarget = useIsFolderMoveTarget(id) && dndNamespace !== null;
  const isFolderUploadTarget = useIsFolderUploadTarget(id);
  const isFolderDndTarget = isFolderMoveTarget || isFolderUploadTarget;
  const pasteClipboard = usePasteAssetClipboard();

  const canCreateChildren = useFolderPermissions({
    check: 'canCreateChildren',
    folderId: id,
  });

  const contextMenuDisabled = useIsContextMenuDisabled() || !canCreateChildren;
  const openNavigationMenu = useOpenNavigationMenu();
  const iconRef = useRef<HTMLButtonElement | null>(null);

  const toggleItem = useToggleItem();

  const href = useMemo(
    () =>
      shouldRenderLinks
        ? getLink({
            page: 'folder',
            projectId: projectId ?? '',
          })
        : undefined,
    [projectId, shouldRenderLinks]
  );

  const isDisabled = transition === 'close';

  const handleToggle = () => {
    toggleItem?.(item, !isOpen);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    let didHandleEvent = false;

    if (event.key === 'Enter') {
      onItemSelect(item, href);
      didHandleEvent = true;
    }

    if (event.metaKey && event.key === 'v') {
      pasteClipboard(id);
      didHandleEvent = true;
    }

    if (event.key === 'ArrowRight' && !isOpen) {
      toggleItem?.(item, true);
      didHandleEvent = true;
    }

    if (event.key === 'ArrowLeft' && isOpen) {
      toggleItem?.(item, false);
      didHandleEvent = true;
    }

    if (didHandleEvent) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.button === 2 || event.metaKey) return;

      onItemSelect(item, href);
    },
    [href, item, onItemSelect]
  );

  const handleContextMenu = (event?: React.MouseEvent) => {
    event?.preventDefault();

    if (contextMenuDisabled) return false;

    openNavigationMenu(item, iconRef, event);

    return true;
  };

  /**
   * when an item is dragged over this folder, if it's closed start a
   * timer. when the timer is up, toggle it open
   */
  const forceDndRemeasure = useForceGlobalDndRemeasure();

  useEffect(() => {
    let timer: number;
    if (!isOpen && isFolderDndTarget) {
      timer = window.setTimeout(() => {
        toggleItem?.(item, true);
        forceDndRemeasure();
      }, LONG_HOLD_TOGGLE_DELAY);
    }
    return () => window.clearTimeout(timer);
  }, [isOpen, isFolderDndTarget, item, toggleItem, forceDndRemeasure]);

  const name =
    typeof overrideRootFolderName === 'string'
      ? overrideRootFolderName
      : TEXT.assets;

  return (
    <DraggableContainer
      key={id}
      $dndVariant={isFolderDndTarget ? 'over' : undefined}
      $isActive={isActive}
      as={BaseContainer}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
    >
      <ItemContainer
        key={id}
        adornmentAfter={
          !contextMenuDisabled && (
            <Tooltip placement="right" title={TEXT.new}>
              <IconButton2
                ref={iconRef}
                icon={<IconPlus />}
                label={TEXT.moreOptions}
                onPress={() => handleContextMenu()}
                size="small"
                variant="tertiary"
              />
            </Tooltip>
          )
        }
        expandable={expandable}
        id={id}
        isActive={isActive}
        isDisabled={isDisabled}
        isLink={shouldRenderLinks}
        isOpen={isOpen}
        name={TEXT.assets}
        onChevronClick={handleToggle}
        type={type}
      >
        <IconFolderImage
          color={theme.colors.icon.primary}
          size={ICON_WIDTH}
          style={{ flexShrink: 0 }}
        />
        <Text
          color={isActive ? 'primary' : undefined}
          truncate={true}
          variant="body"
        >
          {name}
        </Text>

        {shouldRenderLinks && href && (
          <HiddenLink
            aria-label={name ?? undefined}
            href={href}
            tabIndex={-1}
          />
        )}
      </ItemContainer>
    </DraggableContainer>
  );
};

TEXT.DEFINE({
  assets: 'Assets',
  /** aria-label for the more options button for folders in the project tree. */
  moreOptions: 'More options',
  new: 'New',
});
